import { createSelector } from "reselect";

import products from "./products";
import services from "./services";

const resources = {};

/**
 * Returns all resources.
 * @param {Object} state
 * @returns {Object[]} resources
 */
resources.get = state => state.resources.items;

/**
 * Returns uri of current resource.
 * @param {Object} state
 * @returns {string} uri
 */
resources.getCurrentUri = state => state.resources.current;

/**
 * Returns all resources except those for self-referential 'RBAC' service.
 * @param {Object} state
 * @returns {Object[]} resources
 */
resources.getFiltered = createSelector(
  [resources.get],
  resources =>
    resources.filter(resource => {
      if (resource.service) {
        return resource.service.$ref !== "/api/rbac/v1/service/0";
      }
      return true;
    })
);

/**
 * Returns current resource object.
 * @param {object} state
 * @returns {Object} resource
 */
resources.getCurrent = createSelector(
  [resources.getCurrentUri, resources.getFiltered],
  (currentResourceUri, resources) =>
    resources.find(resource => resource.$uri === currentResourceUri)
);

/**
 * If currentService is defined, return resources filtered by currentService,
 * otherwise return all resources.
 * @param {Object} state
 * @returns {Object[]} resources
 */
resources.getByCurrentService = createSelector(
  [services.getCurrent, resources.getFiltered],
  (currentService, resources) => {
    if (!currentService) {
      return resources;
    }
    return resources.filter(
      resource => resource.service.$ref === currentService.$uri
    );
  }
);

/**
 * If currentService is defined, return count of resources filtered by currentService
 * @param {Object} state
 * @returns {Number}
 */
resources.getCountByCurrentService = createSelector(
  [services.getCurrent, resources.getByCurrentService],
  (currentService, resources) => (currentService ? resources.length : 0)
);

/**
 * If currentProduct is defined, return resources filtered by services in currentProduct,
 * otherwise return all resources.
 * @param {Object} state
 * @returns {Object[]} resources
 */
resources.getByCurrentProduct = createSelector(
  [products.getCurrent, services.getFiltered, resources.getByCurrentService],
  (currentProduct, services, resources) => {
    if (!currentProduct) {
      return resources;
    }

    const filteredServices = services.filter(
      service => service.product.$ref === currentProduct.$uri
    );

    return resources.filter(resource => {
      const serviceUris = filteredServices.map(service => service.$uri);
      return serviceUris.includes(resource.service.$ref);
    });
  }
);

export default resources;
